/**
 * Cola entre o renderizador único (`renderTemplate`, em `@troia-v3/types`) e as
 * telas que mostram template.
 *
 * Existe por dois motivos concretos:
 *
 * 1. **A tela de criação monta a configuração aos poucos.** Enquanto o usuário
 *    digita, `providerConfig` pode ser só `{ providerType }` — sem
 *    `messageData`, sem `components`. O renderizador puro confia no formato que
 *    o backend devolve e estoura nesse meio-termo. Aqui o buraco é tapado
 *    ANTES da chamada, para o preview nunca derrubar a página.
 * 2. **Todas as telas precisam dos mesmos rótulos.** Variável sem valor aparece
 *    como `[Nome do Cliente]` em vez de `{{1}}` — e isso vem de
 *    `TemplateVariable.label`, que é a mesma lista em todas elas.
 */
import { renderTemplate } from '@troia-v3/types';
import type {
  EmailTemplateConfig,
  GatewayTemplateConfig,
  InstagramTemplateConfig,
  TemplateProviderConfig,
  TemplateRenderizado,
  TemplateVariable,
  WhatsAppOfficialTemplateConfig,
  WhatsAppTemplateComponent,
} from '@troia-v3/types';
import type { IntegrationId } from '@/shared/icons';

/** Rótulo por posição: `{{1}}` sem valor vira `[Nome do Cliente]` na tela. */
export function rotulosDasVariaveis(variaveis?: TemplateVariable[]): Record<string, string> {
  const rotulos: Record<string, string> = {};
  for (const v of variaveis ?? []) {
    if (v?.label) rotulos[String(v.position)] = v.label;
  }
  return rotulos;
}

/**
 * Valores de exemplo declarados no template, por posição.
 *
 * É o que a tela mostra quando ninguém digitou nada. Usar o exemplo (e não o
 * rótulo entre colchetes) é o que o modal do chat já fazia ao pré-preencher os
 * campos — as outras telas passam a mostrar exatamente o mesmo corpo.
 */
export function valoresDeExemplo(variaveis?: TemplateVariable[]): Record<string, string> {
  const valores: Record<string, string> = {};
  for (const v of variaveis ?? []) {
    if (v?.example) valores[String(v.position)] = v.example;
  }
  return valores;
}

const TIPOS_DE_EMAIL = ['email_smtp', 'email_sendgrid', 'email_resend', 'email_ses', 'gmail_api'];

/** Ícone de integração por providerType — usado no rótulo acima do balão. */
export function integracaoDoProvider(providerType?: string): IntegrationId {
  if (providerType === 'whatsapp_business') return 'whatsapp-business';
  if (providerType === 'whatsapp_gateway') return 'gateway-whatsapp';
  if (providerType === 'instagram_direct') return 'instagram-messaging';
  if (providerType === 'facebook_messenger') return 'facebook-messenger';
  if (providerType && TIPOS_DE_EMAIL.includes(providerType)) return 'email-smtp';
  return 'website-widget';
}

function ehObjeto(valor: unknown): valor is Record<string, unknown> {
  return typeof valor === 'object' && valor !== null;
}

function texto(valor: unknown): string | undefined {
  return typeof valor === 'string' ? valor : undefined;
}

/**
 * Completa o que falta numa configuração meio construída (ou legada) para que o
 * renderizador puro possa trabalhar. Devolve `null` quando não dá nem para
 * saber de que canal se trata — aí a tela mostra o estado vazio.
 */
function completarConfig(bruto: unknown): TemplateProviderConfig | null {
  if (!ehObjeto(bruto)) return null;
  const tipo = texto(bruto['providerType']);
  if (!tipo) return null;

  if (tipo === 'whatsapp_business') {
    const componentes = Array.isArray(bruto['components'])
      ? (bruto['components'] as WhatsAppTemplateComponent[])
      : [];
    const midia = bruto['headerMedia'];
    return {
      providerType: 'whatsapp_business',
      components: componentes,
      ...(ehObjeto(midia) && texto(midia['url']) ? { headerMedia: midia } : {}),
    } as WhatsAppOfficialTemplateConfig;
  }

  if (tipo === 'whatsapp_gateway') {
    const dados = bruto['messageData'];
    return {
      providerType: 'whatsapp_gateway',
      messageData: ehObjeto(dados)
        ? (dados as GatewayTemplateConfig['messageData'])
        : { type: 'text' },
    } as GatewayTemplateConfig;
  }

  if (tipo === 'instagram_direct' || tipo === 'facebook_messenger') {
    const dados = bruto['messageData'];
    return {
      providerType: tipo,
      messageData: ehObjeto(dados) ? (dados as InstagramTemplateConfig['messageData']) : {},
    } as InstagramTemplateConfig;
  }

  if (TIPOS_DE_EMAIL.includes(tipo)) {
    return {
      providerType: tipo as EmailTemplateConfig['providerType'],
      subject: texto(bruto['subject']) ?? '',
      htmlBody: texto(bruto['htmlBody']) ?? '',
      plainTextBody: texto(bruto['plainTextBody']),
    } as EmailTemplateConfig;
  }

  return null;
}

/**
 * Renderiza para a tela. `valores` são os valores já preenchidos por posição
 * (`{ '1': 'Maria' }`); `declaradas` são as variáveis do template, usadas só
 * para o rótulo entre colchetes quando a posição está vazia.
 *
 * Devolve `null` quando não há o que desenhar.
 */
export function renderTemplateSeguro(
  bruto: unknown,
  valores: Record<string, string> = {},
  declaradas?: TemplateVariable[],
): TemplateRenderizado | null {
  const config = completarConfig(bruto);
  if (!config) return null;

  const rotulos = rotulosDasVariaveis(declaradas);
  try {
    return renderTemplate(
      config,
      valores,
      Object.keys(rotulos).length > 0 ? { rotulos } : undefined,
    );
  } catch (erro) {
    // Preview não vale uma tela branca — mas o erro não pode sumir em silêncio.
    console.error('[TemplatePreview] falha ao renderizar o template', erro);
    return null;
  }
}

/** Verdadeiro quando o cabeçalho carrega arquivo (imagem, vídeo ou documento). */
export function cabecalhoTemArquivo(render: TemplateRenderizado | null): boolean {
  const formato = render?.cabecalho?.formato;
  return formato === 'imagem' || formato === 'video' || formato === 'documento';
}
